/**
 * MetricCard — the universal card. States:
 *  - ready: headline value (+ delta/sub), clickable to drill in.
 *  - not-ready: a notice + a link to the input it needs — and, when `sample`
 *    is provided, representative numbers behind a SAMPLE badge so the card's
 *    DESIGN stays reviewable (founder 2026-06-12; never silent fake numbers).
 *  - the ⓘ explain + ↗ drill affordance.
 */

import { Link } from "react-router-dom";
import { Card, CardHead, SampleBadge } from "./cards.js";
import { useDemoMode } from "../lib/demo-mode.js";
import { Sparkline } from "./Sparkline.js";
import { useCardChart, useRatioTrend, type RatioKey } from "./CardTrend.js";
import type { DrillMetric } from "./DrillExplorer.js";
import { REQUIREMENTS, CAPABILITY_NEED, firstMissing, type Capability, type Capabilities } from "../lib/card-requirements.js";
import { NeedWarning } from "./NeedWarning.js";
import { usePeriod } from "../lib/period.js";
import { moneyFmt } from "../lib/format.js";
import { useHide, cardId } from "../lib/hide.js";
import { useCardNameOverride } from "../lib/card-approval.js";

export function shouldLoadRatioTrend(input: {
  missing: Capability | null | undefined;
  ratio: RatioKey | undefined;
  value: string | undefined;
  periodKey: ReturnType<typeof usePeriod>["key"];
}): boolean {
  const ready = input.missing === null || input.missing === undefined;
  return ready
    && input.ratio !== undefined
    && input.value !== undefined
    && input.periodKey !== "30d";
}

export function MetricCard({
  title,
  value,
  sub,
  delta,
  explain,
  requires,
  caps,
  onClick,
  id,
  sample,
  sampleUntilBuilt,
  spark,
  goodIsDown,
  trendMetric,
  trendRatio,
  chartKey,
  loading,
  footerLink,
  headerExtra,
  trustWarning,
}: {
  title: string;
  value?: string;
  sub?: string;
  /** A small control (e.g. a Revenue/CM toggle) rendered in the card header,
   *  left of the chart menu. The parent owns its state. */
  headerExtra?: React.ReactNode;
  delta?: number | null;
  explain?: string[];
  requires?: Capability[];
  caps?: Capabilities;
  onClick?: () => void;
  id?: string;
  /** Give the card the SAME chart-type dropdown + trend as the KPI cards, fed
   *  from the period-aware breakdown for this metric (founder 2026-06-13:
   *  "our recent changes have to apply to ALL cards"). Omit for ratio/locked
   *  cards with no honest time series (MER, CAC, LTV) — they stay number-only. */
  trendMetric?: DrillMetric;
  /** Blended ratio (MER/aMER/CAC) → a by-MONTH trend once the window is longer
   *  than 30 days (founder 2026-06-14: "see MER by month if I choose more than
   *  30 days"). At 30 days the card stays number-only. */
  trendRatio?: RatioKey;
  /** Chart-pref key; defaults to the card id. */
  chartKey?: string;
  /** Representative numbers shown — with a SAMPLE badge — when the card is
   *  locked (requires unmet), or always when sampleUntilBuilt is set. */
  sample?: { value: string; sub?: string };
  /** Set ONLY on cards whose metric backend doesn't exist yet (value is
   *  permanently undefined) — renders the sample in the unlocked state too.
   *  Never set this on a card with a real query: value===undefined also
   *  happens while loading, and a SAMPLE flash before real data is a lie
   *  (Codex review). Remove the flag when the backend ships. */
  sampleUntilBuilt?: boolean;
  /** Mini trendline under the number (real series only — never decorative). */
  spark?: number[];
  /** Invert delta colors: a RISING number is bad (refund rate, churn, CAC). */
  goodIsDown?: boolean;
  /** VESTIGIAL (founder 2026-07-14): this card is inherently blended (its number is
   *  built from account-level spend/overhead, not tracked per product). The floating
   *  "Blended" badge that used to key off this prop was removed — the blended signal
   *  now lives in the card TITLE and ⓘ note. The prop is still accepted (many call
   *  sites pass it) but no longer drives any UI. Safe to drop from call sites over time. */
  companyWide?: boolean;
  /** While the card's own data query is still in flight, show a loading shimmer
   *  instead of the value — which would otherwise flash a fake "—"/$0 before the
   *  real data lands (the loading-gap lie flagged in the sampleUntilBuilt note).
   *  Opt-in; off by default so every existing card is unchanged. */
  loading?: boolean;
  /** A small "→" link rendered at the bottom of the ready card (stops propagation
   *  so it never also fires the card's drill). Used to send a blended ratio (LTV:CAC)
   *  to a page where it CAN be split — e.g. "View by channel →" → the Customer LTV
   *  page's Source tab (founder's blended model 2026-07-13). */
  footerLink?: { label: string; to?: string; onClick?: () => void };
  /** Prominent source-data warning shown inside the card without replacing a
   *  safely calculated value. */
  trustWarning?: React.ReactNode;
}): React.ReactElement | null {
  const hide = useHide();
  const demo = useDemoMode();
  const cid = id ?? cardId(title);
  // The operator's rename for THIS card (keyed by its render id) wins over the
  // hardcoded title everywhere the card draws it. Undefined when not renamed.
  const shownTitle = useCardNameOverride(cid) ?? title;
  const isHidden = hide?.hidden.has(cid) ?? false;
  const missing = firstMissing(requires, caps);
  // A blended ratio (MER/aMER/CAC) gets a by-MONTH trend ONLY past 30 days
  // (founder 2026-06-14); at 30 days it stays number-only. Fetched from the
  // dedicated ratio-trend endpoint, formatted as "×" (or $ for CAC).
  const period = usePeriod();
  // The ratio-trends endpoint is intentionally expensive. Wait for the card's
  // headline request to finish first so a cold Marketing load cannot starve the
  // headline CAC/MER request and leave the user with dashes or fake $0 values.
  const ratioTrend = useRatioTrend(trendRatio, shouldLoadRatioTrend({
    missing,
    ratio: trendRatio,
    value,
    periodKey: period.key,
  }));
  const ratioFmt = trendRatio === "cac" || trendRatio === "spend" ? moneyFmt : (n: number): string => `${n.toFixed(2)}×`;
  // Trend block (dropdown + chart + rolling avg) — only fetched once the card is
  // unlocked, so locked cards stay cheap. Same source as the KPI cards.
  const chart = useCardChart({
    metric: trendMetric,
    trend: trendRatio ? ratioTrend : undefined,
    fmt: trendRatio ? ratioFmt : undefined,
    // Ratio cards (MER/aMER/CAC) default to the TABLE beyond 30 days
    // (founder 2026-06-14); the chart stays one dropdown-click away.
    defaultChart: trendRatio ? "table" : undefined,
    chartKey: `card-${chartKey ?? cid}`,
    enabled: !missing,
  });

  // Public demo: drop locked/"connect a platform" sample cards entirely —
  // a no-login showcase shouldn't nag visitors to wire up integrations.
  if (missing && demo) return null;

  // Operator hid this card for real client accounts (card approvals) → gone, hard:
  // no restore stub, not revealable in Customize mode. Always empty on demo/test/
  // resettable accounts, so the founder still sees everything to evaluate it.
  if (hide?.forced.has(cid)) return null;

  // Hidden + not customizing → gone. Customizing → show a slim restore card.
  if (hide && isHidden && !hide.editing) return null;
  if (hide?.editing && isHidden) {
    return (
      <Card anchor={cid} style={{ marginBottom: 0, height: "100%", opacity: 0.6, borderStyle: "dashed" }}>
        <CardHead title={shownTitle} />
        <button onClick={() => hide.toggle(cid)} style={hideBtn}>+ Show</button>
      </Card>
    );
  }

  if (missing) {
    // Does this unmet capability map to a founder-fixable NEED? If so, show the
    // ONE unified warning (amber, plain-language, one-click fix) — never a
    // pretend sample number: the founder killed sample fill-ins on gated cards
    // (2026-07-13), extending his profit-model ruling app-wide. Demo/test
    // accounts have seeded data, so their cards never land here. A capability
    // that ISN'T a need (channel/email/chargebacks — "connect this optional
    // platform / coming soon") keeps its own connect notice, still no fake number.
    const needId = CAPABILITY_NEED[missing];
    const req = REQUIREMENTS[missing];
    return (
      <Card anchor={cid} style={{ marginBottom: 0, height: "100%", position: "relative" }}>
        {hide?.editing && <button onClick={() => hide.toggle(cid)} title="Hide" style={hideX}>✕</button>}
        <CardHead title={shownTitle} explain={explain} />
        <div style={{ marginTop: 10 }}>
          {needId ? (
            <NeedWarning need={needId} cmGap={needId === "product_costs" ? caps?.cmGap : null} />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, color: "var(--body-faint)", fontSize: 12.5, lineHeight: 1.45 }}>
                <span style={{ fontSize: 14 }}>🔒</span>
                <span>{req.notice}</span>
              </div>
              <Link to={req.to} style={ctaStyle}>{req.cta} →</Link>
            </div>
          )}
        </div>
      </Card>
    );
  }

  // PARTIAL costing (founder 2026-07-11): the account HAS cost inputs (so the
  // card isn't locked) but some products are selling with none — the true
  // contribution margin can't be computed, so a CM-derived number here would be
  // WRONG, not approximate. Show the amber warning instead of the number — no
  // estimate, no coverage % — with the one-click path into the costs chat.
  // Applies automatically to EVERY card that declares contributionMargin in its
  // requires (the founder's apply-to-all-cards law). This is the universal
  // "we need something → open a chat" pattern — see CmGapNotice.tsx.
  const cmGap = requires?.includes("contributionMargin") ? caps?.cmGap ?? null : null;
  if (cmGap && cmGap.missingProducts > 0) {
    return (
      <Card anchor={cid} style={{ marginBottom: 0, height: "100%", position: "relative" }}>
        {hide?.editing && <button onClick={() => hide.toggle(cid)} title="Hide" style={hideX}>✕</button>}
        <CardHead title={shownTitle} explain={explain} />
        <div style={{ marginTop: 10 }}>
          <NeedWarning need="product_costs" cmGap={cmGap} />
        </div>
      </Card>
    );
  }

  // Unlocked + the metric backend doesn't exist yet (value is PERMANENTLY
  // undefined — explicit flag, never inferred from a loading gap) — show the
  // sample so the design is still reviewable, clearly badged.
  if (value === undefined && sample && sampleUntilBuilt) {
    return (
      <Card anchor={cid} style={{ marginBottom: 0, height: "100%", position: "relative" }}>
        {hide?.editing && <button onClick={() => hide.toggle(cid)} title="Hide" style={hideX}>✕</button>}
        <CardHead title={shownTitle} explain={explain} />
        <div className="dd-num" style={{ fontFamily: "var(--font-display)", fontSize: 26, fontWeight: 700, marginTop: 9, color: "var(--num)", display: "flex", alignItems: "center", gap: 8 }}>
          {sample.value} <SampleBadge />
        </div>
        <div style={{ color: "var(--body)", fontSize: 11.5, marginTop: 5 }}>
          {sample.sub ?? "example numbers — the real metric is coming"}
        </div>
      </Card>
    );
  }

  return (
    <Card
      ref={chart.ref}
      onClick={onClick}
      anchor={cid}
      style={{
        marginBottom: 0,
        height: "100%",
        position: "relative",
        transition: "border-color .12s",
      }}
    >
      {hide?.editing && <button onClick={(e) => { e.stopPropagation(); hide.toggle(cid); }} title="Hide" style={hideX}>✕</button>}
      {/* Drill ↗ is rendered by CardHead right after the ⓘ (founder 2026-07-06);
          the chart dropdown stays alone on the right, no clash. */}
      <CardHead title={shownTitle} explain={explain}>
        {/* Stack the mode toggle (Revenue/CM) ABOVE the chart-type menu so the two
            controls don't crowd the header side-by-side (founder 2026-07-14). */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
          {headerExtra}
          {chart.menu}
        </div>
      </CardHead>
      {trustWarning}
      {loading ? (
        // Data still in flight — pulse a placeholder instead of flashing a fake
        // "—"/$0 (the loading-gap lie). Header above stays so the card doesn't jump.
        <div style={{ marginTop: 9 }} aria-busy="true" aria-label={`${title} loading`}>
          <div className="dd-skeleton" style={{ height: 26, width: 104, borderRadius: 6 }} />
          <div className="dd-skeleton" style={{ height: 11, width: 132, borderRadius: 5, marginTop: 13 }} />
        </div>
      ) : (
        <>
          <div className="dd-num" style={{ fontFamily: "var(--font-display)", fontSize: 26, fontWeight: 700, marginTop: 9, color: "var(--num)" }}>
            {value ?? "—"}
          </div>
          <div style={{ display: "flex", gap: 9, marginTop: 7, alignItems: "center" }}>
            {delta !== null && delta !== undefined && (
              <span className="dd-num" style={{ fontSize: 12.5, fontWeight: 700, color: (goodIsDown ? delta <= 0 : delta >= 0) ? "var(--good)" : "var(--bad)" }}>
                {delta >= 0 ? "▲" : "▼"} {Math.abs(delta * 100).toFixed(1)}%
              </span>
            )}
            {sub && <span style={{ color: "var(--body)", fontSize: 11.5 }}>{sub}</span>}
          </div>
          {/* The "Blended" badge was removed (founder 2026-07-14): the blended
              signal now lives in the card TITLE ("(All Traffic)" / "(Blended)")
              and the ⓘ note, so a floating chip is redundant. Blended-ness is a
              data fact (spend/overhead aren't per-product), not a per-card toggle. */}
          {/* The trend chart (when this card has a metric series); else the legacy
              mini-sparkline if one was passed. Never both. */}
          {chart.body ? chart.body : spark && spark.length > 2 && (
            <div style={{ marginTop: 8 }}>
              <Sparkline data={spark} width={210} height={28} />
            </div>
          )}
          {footerLink && (footerLink.onClick ? (
            <button type="button" onClick={(e) => { e.stopPropagation(); footerLink.onClick?.(); }}
              style={{ display: "inline-block", marginTop: 9, fontSize: 11.5, fontWeight: 600, color: "var(--accent)", textDecoration: "none", background: "none", border: "none", padding: 0, cursor: "pointer" }}>
              {footerLink.label}
            </button>
          ) : (
            <Link to={footerLink.to ?? "#"} onClick={(e) => e.stopPropagation()}
              style={{ display: "inline-block", marginTop: 9, fontSize: 11.5, fontWeight: 600, color: "var(--accent)", textDecoration: "none" }}>
              {footerLink.label}
            </Link>
          ))}
        </>
      )}
    </Card>
  );
}

const hideX: React.CSSProperties = {
  position: "absolute",
  top: 6,
  right: 6,
  zIndex: 5,
  width: 20,
  height: 20,
  borderRadius: 5,
  border: "1px solid var(--border)",
  background: "var(--surface)",
  color: "var(--body)",
  fontSize: 11,
  lineHeight: 1,
  cursor: "pointer",
};
const hideBtn: React.CSSProperties = {
  marginTop: 10,
  border: "1px solid var(--border)",
  background: "var(--surface-2)",
  color: "var(--text)",
  borderRadius: "var(--r-sm)",
  padding: "5px 11px",
  fontSize: 12.5,
  cursor: "pointer",
};
const ctaStyle: React.CSSProperties = {
  display: "inline-flex",
  alignSelf: "flex-start",
  alignItems: "center",
  gap: 5,
  border: "1px solid color-mix(in srgb, var(--accent) 45%, var(--border))",
  background: "color-mix(in srgb, var(--accent) 12%, var(--surface))",
  color: "var(--accent)",
  borderRadius: "var(--r-sm)",
  padding: "6px 11px",
  fontSize: 12.5,
  fontWeight: 600,
  textDecoration: "none",
};
